/**
 * Parse Firestore timestamps, serialized cache objects, ISO strings, and numbers.
 * Returns null when the value cannot be parsed to a valid Date.
 */
export function toJSDate(value) {
  if (value == null) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value?.toDate === 'function') {
    const date = value.toDate();
    return date && !Number.isNaN(date.getTime()) ? date : null;
  }

  if (typeof value?.toMillis === 'function') {
    const ms = value.toMillis();
    if (typeof ms === 'number' && !Number.isNaN(ms)) {
      const date = new Date(ms);
      return Number.isNaN(date.getTime()) ? null : date;
    }
  }

  // Session/cache marker from ChooseArtworkModal serialization
  if (value?.__amTs === true && value.s != null) {
    const seconds = Number(value.s);
    const nanos = Number(value.n || 0);
    if (!Number.isNaN(seconds)) {
      const date = new Date(seconds * 1000 + nanos / 1000000);
      return Number.isNaN(date.getTime()) ? null : date;
    }
  }

  if (value?.__amDate === true && typeof value.iso === 'string') {
    return toJSDate(value.iso);
  }

  const secondsRaw =
    value?.seconds ?? value?._seconds ?? value?.s ?? null;
  const seconds =
    typeof secondsRaw === 'number'
      ? secondsRaw
      : typeof secondsRaw === 'string' && secondsRaw.trim() !== ''
        ? Number(secondsRaw)
        : null;

  if (seconds != null && !Number.isNaN(seconds)) {
    const nanosRaw =
      value?.nanoseconds ?? value?._nanoseconds ?? value?.n ?? 0;
    const nanos =
      typeof nanosRaw === 'number'
        ? nanosRaw
        : typeof nanosRaw === 'string' && nanosRaw.trim() !== ''
          ? Number(nanosRaw)
          : 0;
    const date = new Date(seconds * 1000 + (Number.isNaN(nanos) ? 0 : nanos) / 1000000);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof value === 'number') {
    // Treat small numbers as seconds since epoch; large as milliseconds
    const ms = value < 1e12 ? value * 1000 : value;
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const asNum = Number(trimmed);
    if (!Number.isNaN(asNum) && /^\d+(\.\d+)?$/.test(trimmed)) {
      const ms = asNum < 1e12 ? asNum * 1000 : asNum;
      const date = new Date(ms);
      return Number.isNaN(date.getTime()) ? null : date;
    }
    const date = new Date(trimmed);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  return null;
}

/** Best available date field on an activity record. */
export function getActivityDate(activity) {
  if (!activity || typeof activity !== 'object') return null;

  const candidates = [
    activity.createdAt,
    activity.updatedAt,
    activity.completedAt,
    activity.timestamp,
    activity.metadata?.createdAt,
    activity.metadata?.savedAt,
    activity.metadata?.completedAt,
  ];

  for (const candidate of candidates) {
    const date = toJSDate(candidate);
    if (date) return date;
  }

  return null;
}

const DEFAULT_DATE_OPTIONS = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
};

const DEFAULT_DATETIME_OPTIONS = {
  ...DEFAULT_DATE_OPTIONS,
  hour: '2-digit',
  minute: '2-digit',
};

/**
 * Format a timestamp or activity for display. Never returns "Invalid Date" or "Today".
 */
export function formatActivityDate(value, options = {}) {
  const { includeTime = false, fallback = '—' } = options;

  let date = null;
  if (value && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
    const hasDateField =
      'createdAt' in value ||
      'updatedAt' in value ||
      'completedAt' in value ||
      'timestamp' in value;
    date = hasDateField ? getActivityDate(value) : toJSDate(value);
  } else {
    date = toJSDate(value);
  }

  if (!date || Number.isNaN(date.getTime())) return fallback;

  try {
    const formatted = date.toLocaleDateString(
      'en-US',
      includeTime ? DEFAULT_DATETIME_OPTIONS : DEFAULT_DATE_OPTIONS
    );
    if (!formatted || formatted === 'Invalid Date') return fallback;
    return formatted;
  } catch {
    return fallback;
  }
}

/** Normalize timestamp fields on an activity after Firestore/cache round-trip. */
export function normalizeActivityTimestamps(activity) {
  if (!activity || typeof activity !== 'object') return activity;
  const next = { ...activity };
  for (const key of ['createdAt', 'updatedAt', 'completedAt', 'timestamp']) {
    if (key in next && next[key] != null) {
      const parsed = toJSDate(next[key]);
      if (parsed) next[key] = parsed.toISOString();
    }
  }
  return next;
}
